import React from "react";
import { API_BASE } from "../config";

// --- Utility Functions ---

function numberToWords(num) {
  if (num == null) return "-";
  num = Number(num);
  if (isNaN(num)) return "-";

  const ones = ['', 'One', 'Two', 'Three', 'Four', 'Five', 'Six', 'Seven', 'Eight', 'Nine', 'Ten', 'Eleven',
    'Twelve', 'Thirteen', 'Fourteen', 'Fifteen', 'Sixteen', 'Seventeen', 'Eighteen', 'Nineteen'];
  const tens = ['', '', 'Twenty', 'Thirty', 'Forty', 'Fifty', 'Sixty', 'Seventy', 'Eighty', 'Ninety'];
  const scales = ['', 'Thousand', 'Lakh', 'Crore'];

  function chunkToWords(n) {
    let word = "";
    if (n > 99) {
      word += ones[Math.floor(n / 100)] + " Hundred";
      n = n % 100;
      if (n) word += " and ";
    }
    if (n < 20) {
      word += ones[n];
    } else {
      word += tens[Math.floor(n / 10)];
      if (n % 10) word += " " + ones[n % 10];
    }
    return word;
  }

  function splitIndianNumber(n) {
    const str = n.toString();
    let chunks = [];
    let end = str.length;
    chunks.push(parseInt(str.substring(Math.max(0, end - 3), end), 10));
    end -= 3;
    while (end > 0) {
      let start = Math.max(0, end - 2);
      chunks.push(parseInt(str.substring(start, end), 10));
      end -= 2;
    }
    return chunks;
  }

  if (num === 0) return "Zero";
  const numberChunks = splitIndianNumber(parseInt(num));
  let wordArray = [];
  for (let i = 0; i < numberChunks.length; i++) {
    if (numberChunks[i] > 0) {
      wordArray.push(chunkToWords(numberChunks[i]) + (scales[i] ? " " + scales[i] : ""));
    }
  }
  return wordArray.reverse().join(" ").trim();
}

function formatDate(dateStr) {
  if (!dateStr) return "-";
  const d = new Date(dateStr);
  if (isNaN(d.getTime())) return "-";
  return `${d.getDate().toString().padStart(2, "0")}/${(d.getMonth() + 1)
    .toString()
    .padStart(2, "0")}/${d.getFullYear()}`;
}

const DischargeReceiptModal = ({
  show,
  data,
  patient,
  dischargeDetails,
  companyDetails,
  advanceDetails,
  onClose,
  onPrint,
}) => {
  if (!show || !data || !companyDetails) return null;

  const rawResponse = data?.rawServerResponse || {};
  const deepDischarge = rawResponse.dischargeDetails || rawResponse.discharge || {};
  const deepIPD = deepDischarge.ipd || rawResponse.patientDetails || rawResponse.patient || {}; 

  const billNo = deepDischarge.invNo || dischargeDetails?.invNo || deepDischarge.InvNo || dischargeDetails?.InvNo || "-";
  
  // Admit Date & Discharge Date
  const admitDateRaw = deepIPD.ipD_Date || deepIPD.IPD_Date || patient?.regDate;
  const dischargeDateRaw = data.dischargeDate || deepDischarge.disdate || deepDischarge.Disdate;
  const noOfDays = deepDischarge.nofdays || deepDischarge.Nofdays || data.nofdays || "1";
  const patientStatus = deepDischarge.patientsSTS || "Alive";

  const rawServices = data?.services || rawResponse.serviceDetails || [];
  const roomDetails = data?.roomDetails || rawResponse.roomDetails || [];
  
  const totalAdvance = (advanceDetails || []).reduce((sum, item) => sum + (Number(item.adv_Amt) || 0), 0);
  const receivedNow = Number(data.received || 0);

  // ✅ LOGIC: CONSOLIDATE SERVICES
  const serviceMap = {};
  rawServices.forEach((s) => {
    const key = s.serviceName || s.name || "Unknown Service";
    if (!serviceMap[key]) {
      serviceMap[key] = {
        description: key,
        rate: Number(s.servRate || s.rate || 0),
        qty: 0,
        amount: 0
      };
    }
    const currentQty = Number(s.qty || s.QTY || s.unit || 1);
    serviceMap[key].qty += currentQty;
    serviceMap[key].amount += Number(s.amount || (serviceMap[key].rate * currentQty));
  });
  
  const consolidatedServicesArray = Object.values(serviceMap);

 // Merge Rooms & Consolidated Services
  const allItems = [
    ...roomDetails.map((r) => {
      // 🟢 THEEK KIYA HUA LOGIC (Issue 3 Fix)
      let days = Number(r.qty); 
      let roomRate = Number(r.roomRate || r.RoomRate || 0);
      let calculatedAmt = Number(r.amount || r.Amount || 0);

      // Agar C# ne 'qty' ya 'amount' nahi bheji hai, toh fallback 24-hour calculation yahin kar do
      if (!days && (r.entryDate || r.EntryDate)) {
        const entry = new Date(r.entryDate || r.EntryDate);
        let exit = (r.exitDate || r.ExitDate) 
                   ? new Date(r.exitDate || r.ExitDate) 
                   : (() => { 
                       const d = new Date(dischargeDateRaw); 
                       const now = new Date(); 
                       d.setHours(now.getHours(), now.getMinutes(), 0, 0); 
                       return d; 
                     })();
        const diffHours = (exit.getTime() - entry.getTime()) / (1000 * 60 * 60);
        days = Math.max(1, Math.ceil(diffHours / 24));
        calculatedAmt = days * roomRate;
      } else {
        // Agar days hai par by chance 0 ya NaN hai, to min 1 le lo
        days = Math.max(1, days || 1);
        // Agar calculatedAmt 0 hai, tabhi multiply karo, varna C# wala amount rahne do
        if(!calculatedAmt) calculatedAmt = days * roomRate; 
      }

      return {
        description: `Room Charges - ${r.roomCatge || r.RoomCatge || "General"} (${r.roomNo || r.RoomID || "-"})`,
        rate: roomRate,
        qty: days,
        amount: calculatedAmt,
      };
    }),
    ...consolidatedServicesArray,
  ].map((item, index) => ({ ...item, sr: index + 1 }));
  // ✅ LOGIC: PAGINATION & PAGE TOTALS (20 ITEMS PER PAGE TO AVOID CUTTING)
  const ITEMS_PER_PAGE = 20; 
  const pages = [];
  let runningTotal = 0;

  for (let i = 0; i < allItems.length; i += ITEMS_PER_PAGE) {
    const pageItems = allItems.slice(i, i + ITEMS_PER_PAGE);
   const pageTotal = pageItems.reduce((sum, item) => sum + (Number(item.amount) || 0), 0);
    
    pages.push({
      items: pageItems,
      broughtForward: runningTotal, 
      pageTotal: pageTotal,        
      carriedForward: runningTotal + pageTotal 
    });

    runningTotal += pageTotal;
  }

  if (pages.length === 0) {
    pages.push({ items: [], broughtForward: 0, pageTotal: 0, carriedForward: 0 });
  }
 // 🌐 Base URL for dynamic images (Bulletproof method)
  const getImageUrl = (path) => {
    if (!path) return "";
    const base = API_BASE.replace(/\/api\/?$/, ''); 
    const cleanPath = path.replace(/^\//, ''); 
    return `${base}/${cleanPath}`;
  };

  // ✅ SAFELY HANDLE 'logo' OR 'Logo' (Case Sensitivity Fix)
  const mainLogoPath = companyDetails?.logo || companyDetails?.Logo;
  const ayushmanLogoPath = companyDetails?.ayushmanLogo || companyDetails?.AyushmanLogo;

  return (
    <>
      {/* =========================================
         INLINE CSS: PRINT & MARG BILLING STYLES
      ========================================= */}
      <style>{`
        :root {
          --marg-border: 1px solid #000;
          --marg-border-thick: 2px solid #000;
          --marg-font: Arial, Helvetica, sans-serif;
        }

        .marg-receipt-wrapper {
          font-family: var(--marg-font);
          color: #000;
          background: #fff;
          font-size: 12px;
        }

        /* ✅ FIX: Ensure padding and borders don't break the percentage widths */
        .marg-receipt-wrapper * {
          box-sizing: border-box;
        }

        .marg-outer-box {
          border: var(--marg-border-thick);
          margin-bottom: 20px;
          height: 265mm; /* FIXED HEIGHT FOR A4 SO IT NEVER CLIPS */
          max-height: 265mm;
          box-sizing: border-box;
          position: relative;
          background: #fff;
          display: flex;
          flex-direction: column;
          page-break-inside: avoid;
        }

        .marg-header {
          border-bottom: var(--marg-border-thick);
          padding: 8px 10px;
          text-align: center;
          position: relative;
        }
        
        .marg-title-strip {
          background: #f0f0f0;
          border-bottom: var(--marg-border-thick);
          text-align: center;
          padding: 4px;
          font-weight: 900;
          font-size: 14px;
          letter-spacing: 1px;
        }

        .marg-patient-box {
          display: flex;
          border-bottom: var(--marg-border-thick);
        }

        .marg-patient-col { padding: 5px 10px; }
        
        /* ✅ ALIGNMENT FIX: 70% Left Side */
        .marg-patient-left { width: 70%; border-right: var(--marg-border-thick); }
        /* ✅ ALIGNMENT FIX: 30% Right Side */
        .marg-patient-right { width: 30%; }

        .marg-patient-col table {
          width: 100%;
          font-size: 12px;
          line-height: 1.4;
        }
        .marg-patient-col table td { vertical-align: top; }

        /* Items Table */
        .marg-table-container {
          flex-grow: 1; /* PUSHES FOOTER TO BOTTOM */
          display: flex;
          flex-direction: column;
        }

        .marg-table-header {
          display: flex;
          width: 100%;
          border-bottom: var(--marg-border);
          font-weight: bold;
          text-transform: uppercase;
          font-size: 11px;
          background: #f9f9f9;
        }

        .marg-table-row {
          display: flex;
          width: 100%;
          font-size: 12px;
        }

        /* ✅ FIX: align-items: stretch forces the internal divs to reach the bottom */
        .marg-table-filler {
          flex-grow: 1;
          display: flex;
          width: 100%;
          align-items: stretch;
        }

        /* ✅ ALIGNMENT FIX: 6% + 64% = 70% (Matches Patient Box & Footer Left Side) */
        .marg-col-sr   { width: 6%;  text-align: center; padding: 4px 5px; border-right: var(--marg-border); }
        .marg-col-desc { width: 64%; text-align: left;   padding: 4px 5px; border-right: var(--marg-border); }
        
        /* ✅ ALIGNMENT FIX: 10% + 6% + 14% = 30% (Matches Patient Box & Footer Right Side) */
        .marg-col-rate { width: 10%; text-align: right;  padding: 4px 5px; border-right: var(--marg-border); }
        .marg-col-qty  { width: 6%;  text-align: center; padding: 4px 5px; border-right: var(--marg-border); }
        .marg-col-amt  { width: 14%; text-align: right;  padding: 4px 5px; }

        /* Page Total Row */
        .marg-page-total {
          display: flex;
          width: 100%;
          border-top: var(--marg-border);
          border-bottom: var(--marg-border-thick);
          font-weight: bold;
          font-size: 12px;
          background: #f9f9f9;
        }

        /* Footer Sections */
        .marg-footer-grid {
          display: flex;
          border-top: var(--marg-border-thick);
        }

        .marg-words-box {
          width: 70%; /* ✅ Matches 70% left column alignment */
          border-right: var(--marg-border-thick);
          padding: 10px;
        }

        .marg-calc-box { width: 30%; } /* ✅ Matches 30% right column alignment */
        
        .marg-calc-table { width: 100%; border-collapse: collapse; }
        .marg-calc-table td { padding: 4px 10px; border-bottom: var(--marg-border); font-size: 12px; }
        .marg-calc-table tr:last-child td { border-bottom: none; }

        /* Signatures - Fixed positioning at bottom */
        .marg-signature-box {
          display: flex;
          justify-content: space-between;
          padding: 35px 20px 10px 20px;
          border-top: var(--marg-border-thick);
          background: #fff;
        }

        .marg-sig-line {
          border-top: 1px solid #000;
          padding-top: 5px;
          text-align: center;
          font-weight: bold;
          font-size: 11px;
          width: 100%;
        }

        /* =========================================
           PRINT MEDIA QUERIES 
        ========================================= */
        @media print {
          @page { size: A4 portrait; margin: 0 5mm 5mm 5mm; }
          html, body {
            height: auto !important;
            overflow: visible !important;
            background: white !important;
            margin: 0 !important;
            padding: 0 !important;
          }
          body * { visibility: hidden; }
          #marg-print-section, #marg-print-section * { visibility: visible; }
          #marg-print-section {
            position: absolute;
            left: 0; top: 0; width: 100%; margin: 0; padding: 0;
          }
          .modal, .modal-dialog, .modal-content {
            border: none !important; box-shadow: none !important;
            background: transparent !important; padding: 0 !important;
          }
          .no-print { display: none !important; }
          .page-break {
            page-break-after: always;
            border-bottom: none !important; margin-bottom: 0 !important;
          }
          .marg-title-strip, .marg-table-header, .marg-page-total {
            -webkit-print-color-adjust: exact; print-color-adjust: exact;
          }
        }
      `}</style>

      <div className="modal fade show" style={{ display: "block", backgroundColor: "rgba(0,0,0,0.6)" }}>
        {/* ✅ Popup Width Increased */}
      <div className="modal-dialog modal-lg" style={{ maxWidth: '900px', margin: '0 auto' }}>
          <div className="modal-content border-0">
            
            <div id="marg-print-section" className="marg-receipt-wrapper p-2 bg-white">
              
              {pages.map((pageData, pageIndex) => (
                <div key={pageIndex} className={`marg-outer-box ${pageIndex < pages.length - 1 ? 'page-break' : ''}`}>
      {mainLogoPath && <img src={getImageUrl(mainLogoPath)}
       alt="watermark" style={{ position: 'absolute', top: '55%', left: '50%', transform: 'translate(-50%, -50%)', width: '350px', opacity: 0.08, pointerEvents: 'none', zIndex: 0, WebkitPrintColorAdjust: 'exact', printColorAdjust: 'exact' }} />}



                  <div className="marg-header">
                  {mainLogoPath && (
  <img 
    src={getImageUrl(mainLogoPath)} 
    alt="Logo" 
    style={{ position: 'absolute', left: '15px', top: '10px', width: '70px', height: '70px', objectFit: 'contain' }} 
  />
)}
                    <h2 style={{ margin: '0 0 5px 0', fontWeight: '900', fontSize: '22px', textTransform: 'uppercase' }}>
                      {companyDetails.company_Name}
                    </h2>
                    <p style={{ margin: '0', fontSize: '13px' }}>{companyDetails.address}</p>
                    <p style={{ margin: '2px 0 0 0', fontSize: '12px' }}>
                      <strong>Ph:</strong> {companyDetails.contact1} &nbsp;|&nbsp; <strong>Email:</strong> {companyDetails.email_ID}
                    </p>
                    {companyDetails.reg_No && (
                      <p style={{ margin: '2px 0 0 0', fontSize: '11px' }}><strong>Reg No:</strong> {companyDetails.reg_No}</p>
                    )}



    {companyDetails?.isAyushmanActive && ayushmanLogoPath && (
  <img 
    src={getImageUrl(ayushmanLogoPath)} 
    alt="Ayushman" 
    style={{ position: 'absolute', right: '10px', top: '0px', width: '120px', height: '120px', objectFit: 'contain' }} 
  />
)}
                    {pages.length > 1 && (
                      <div style={{ position: 'absolute', right: '15px', top: '10px', fontSize: '11px', fontWeight: 'bold' }}>
                        Page {pageIndex + 1} of {pages.length}
                      </div>
                    )}
                  </div>

                  <div className="marg-title-strip">
                    FINAL DISCHARGE BILL
                  </div>

                  {/* --- Patient Info Box --- */}
                  <div className="marg-patient-box">
                    <div className="marg-patient-col marg-patient-left">
                      <table>
                        <tbody>
                          <tr>
                            <td width="30%"><strong>Patient Name</strong></td>
                            <td width="5%">:</td>
                            <td><strong style={{ fontSize: '14px', textTransform: 'uppercase' }}>{patient?.name || "-"}</strong></td>
                          </tr>
                          <tr>
                            <td><strong>UHID No.</strong></td>
                            <td>:</td>
                            <td>{patient?.uhidNo || patient?.uhiD_No || "-"}</td>
                          </tr>
                            <tr>
                            <td><strong>Mobile No.</strong></td>
                            <td>:</td>
                            <td>{patient?.mobileNo || "-"}</td>
                          </tr>

                          <tr>
                            <td><strong>Care Of</strong></td>
                            <td>:</td>
                            <td>{patient?.careOff || "-"}</td>
                          </tr>
                          <tr>
                            <td><strong>Care of Mob</strong></td>
                            <td>:</td>
                            <td>{patient?.careOfMobile || "-"}</td>
                          </tr>
                        
                          <tr>
                            <td><strong>Address</strong></td>
                            <td>:</td>
                            <td>{patient?.address || "-"}</td>
                          </tr>
                        </tbody>
                      </table>
                    </div>
                    
                    {/* ✅ Shifted to Right Side & Perfectly Aligned */}
                    <div className="marg-patient-col marg-patient-right" style={{ display: 'flex', justifyContent: 'flex-end' }}>
                      <table style={{ width: '100%', whiteSpace: 'nowrap' }}>
                        <tbody>
                          <tr>
                            <td style={{ textAlign: 'left' }}><strong>Bill No.</strong></td>
                            <td style={{ textAlign: 'center', padding: '0 4px' }}>:</td>
                            <td style={{ textAlign: 'left' }}><strong>{billNo}</strong></td>
                          </tr>
                          <tr>
                            <td style={{ textAlign: 'left' }}><strong>Admit Date</strong></td>
                            <td style={{ textAlign: 'center', padding: '0 4px' }}>:</td>
                            <td style={{ textAlign: 'left' }}>{formatDate(admitDateRaw)}</td>
                          </tr>
                          <tr>
                            <td style={{ textAlign: 'left' }}><strong>Discharge Date</strong></td>
                            <td style={{ textAlign: 'center', padding: '0 4px' }}>:</td>
                            <td style={{ textAlign: 'left' }}>{formatDate(dischargeDateRaw)}</td>
                          </tr>
                          <tr>
                            <td style={{ textAlign: 'left' }}><strong>Total Days</strong></td>
                            <td style={{ textAlign: 'center', padding: '0 4px' }}>:</td>
                            <td style={{ textAlign: 'left' }}><strong>{noOfDays} Days</strong></td>
                          </tr>
                          <tr>
                            <td style={{ textAlign: 'left' }}><strong>Status</strong></td>
                            <td style={{ textAlign: 'center', padding: '0 4px' }}>:</td>
                            <td style={{ textAlign: 'left' }}>
                              {patientStatus === "Dead" || patientStatus === "Expired" 
                                ? <strong style={{ color: "red" }}>{patientStatus}</strong> 
                                : <strong>{patientStatus}</strong>}
                            </td>
                          </tr>
                        </tbody>
                      </table>
                    </div>
                  </div>

                  {/* --- Bill Items Table --- */}
                  <div className="marg-table-container">
                    
                    <div className="marg-table-header">
                      <div className="marg-col-sr">S.No.</div>
                      <div className="marg-col-desc">Particulars / Services</div>
                      <div className="marg-col-rate">Rate (Rs.)</div>
                      <div className="marg-col-qty">Qty</div>
                      <div className="marg-col-amt">Amount (Rs.)</div>
                    </div>

                    {/* ✅ Brought Forward (If not page 1) */}
                    {pageIndex > 0 && (
                      <div className="marg-page-total" style={{ borderTop: 'none', borderBottom: 'var(--marg-border)' }}>
                        <div className="marg-col-sr"></div>
                        <div className="marg-col-desc" style={{ textAlign: 'right' }}>Brought Forward (B/F):</div>
                        <div className="marg-col-rate"></div>
                        <div className="marg-col-qty"></div>
                        <div className="marg-col-amt">{pageData.broughtForward.toFixed(2)}</div>
                      </div>
                    )}

                    {pageData.items.length > 0 ? (
                      pageData.items.map((item) => (
                        <div className="marg-table-row" key={item.sr}>
                          <div className="marg-col-sr">{item.sr}</div>
                          <div className="marg-col-desc">{item.description}</div>
                          <div className="marg-col-rate">{item.rate.toFixed(2)}</div>
                          <div className="marg-col-qty">{item.qty}</div>
                          <div className="marg-col-amt">{item.amount.toFixed(2)}</div>
                        </div>
                      ))
                    ) : (
                      <div className="marg-table-row" style={{ justifyContent: 'center', padding: '20px' }}>
                        No items billed.
                      </div>
                    )}

                    {/* ✅ Empty filler to stretch vertical lines to the bottom */}
                    <div className="marg-table-filler">
                      <div className="marg-col-sr"></div>
                      <div className="marg-col-desc"></div>
                      <div className="marg-col-rate"></div>
                      <div className="marg-col-qty"></div>
                      <div className="marg-col-amt"></div>
                    </div>
                  </div>

                  {/* ✅ Page Total / Carried Forward */}
                  {pages.length > 1 && (
                    <div className="marg-page-total" style={{ borderTop: 'none' }}>
                      <div className="marg-col-sr"></div>
                      <div className="marg-col-desc" style={{ textAlign: 'right' }}>
                        {pageIndex < pages.length - 1 ? "Carried Forward (C/F):" : "Total of Items:"}
                      </div>
                      <div className="marg-col-rate"></div>
                      <div className="marg-col-qty"></div>
                      <div className="marg-col-amt">{pageData.carriedForward.toFixed(2)}</div>
                    </div>
                  )}

                  {/* --- Summary & Footer (ONLY ON LAST PAGE) --- */}
                  {pageIndex === pages.length - 1 ? (
                    <>
                      <div className="marg-footer-grid">
                        <div className="marg-words-box">
                          <p style={{ margin: "0 0 5px 0", fontSize: "11px" }}><strong>Total Amount in Words:</strong></p>
                          <p style={{ margin: "0", fontSize: "13px", fontWeight: "bold", textTransform: "capitalize" }}>
                            Rupees {numberToWords(Number(data.totalAmount))} Only
                          </p>
                          <div style={{ marginTop: "20px", fontSize: "10px", color: "#444" }}>
                            <p style={{ margin: "0" }}><strong>Terms & Conditions:</strong></p>
                            <ul style={{ paddingLeft: "15px", margin: "2px 0 0 0" }}>
                              <li>All disputes are subject to local jurisdiction.</li>
                              <li>Please keep this bill safe for future reference.</li>
                            </ul>
                          </div>
                        </div>
                        
                        <div className="marg-calc-box">
                          <table className="marg-calc-table">
                            <tbody>
                              <tr>
                                <td>Gross Total</td>
                                <td style={{ textAlign: "right", fontWeight: "bold" }}>{Number(data.totalAmount).toFixed(2)}</td>
                              </tr>
                              <tr>
                                <td>Discount</td>
                                <td style={{ textAlign: "right" }}>- {Number(data.discount).toFixed(2)}</td>
                              </tr>
                               <tr>
                                <td>Advance Paid</td>
                                <td style={{ textAlign: "right" }}>- {totalAdvance.toFixed(2)}</td>
                              </tr>
                              <tr style={{ background: "#f0f0f0" }}>
                                <td><strong>Net Payable</strong></td>
                                <td style={{ textAlign: "right", fontWeight: "bold", fontSize: "14px" }}>{Number(data.payable).toFixed(2)}</td>
                              </tr>
                             
                              <tr>
                                <td>Received Now</td>
                                <td style={{ textAlign: "right" }}>- {receivedNow.toFixed(2)}</td>
                              </tr>
                              <tr style={{ borderTop: "var(--marg-border-thick)" }}>
                                <td><strong>Balance Due</strong></td>
                                <td style={{ textAlign: "right", fontWeight: "bold", fontSize: "14px" }}>{Number(data.due).toFixed(2)}</td>
                              </tr>
                            </tbody>
                          </table>
                        </div>
                      </div>

                      {/* Signatures */}
                      <div className="marg-signature-box">
                        <div style={{ textAlign: "center", width: "28%", display: 'flex', flexDirection: 'column', justifyContent: 'flex-end' }}>
                          <div className="marg-sig-line">Patient / Attendant</div>
                        </div>
                        <div style={{ textAlign: "center", width: "28%", display: 'flex', flexDirection: 'column', justifyContent: 'flex-end' }}>
                          <div style={{ fontSize: '11px', marginBottom: '2px' }}>{deepDischarge.docPreparedby || "Admin"}</div>
                          <div className="marg-sig-line">Prepared By</div>
                        </div>
                        <div style={{ textAlign: "center", width: "28%", display: 'flex', flexDirection: 'column', justifyContent: 'flex-end' }}>
                          <div style={{ fontSize: '11px', marginBottom: '2px' }}>{deepDischarge.disAuthority || "Auth Signatory"}</div>
                          <div className="marg-sig-line">Discount Authority</div>
                        </div>
                      </div>
                    </>
                  ) : (
                    /* Continued text for pages other than the last one */
                    <div style={{ padding: "10px", textAlign: "right", fontWeight: "bold", fontSize: "12px", fontStyle: "italic", borderTop: "var(--marg-border-thick)" }}>
                      Continued to next page...
                    </div>
                  )}

                </div>
              ))}
            </div>

            {/* --- Action Buttons (Hidden in Print) --- */}
            <div className="no-print d-flex justify-content-end gap-2 mt-2 pt-2 border-top bg-white p-3 rounded-bottom">
              <button className="btn btn-secondary px-4" onClick={onClose}>Close</button>
              <button className="btn btn-primary px-4 fw-bold" onClick={onPrint} style={{ background: 'var(--theme-color, #0088cc)', borderColor: 'var(--theme-color, #0088cc)' }}>
                <i className="bi bi-printer me-2"></i> Print Bill
              </button>
            </div>

          </div>
        </div>
      </div>
    </>
  );
};

export default DischargeReceiptModal;